import { AccountIdMeta, WithTodoFragment, completedStatuses } from 'components/dashboard/Reminders'
import {
  AccountOutreachRollupFragment,
  AllTodosDocument,
  TodoItemStatusType,
  useAccountOutreachRollupQuery,
  useDismissReminderMutation,
  useRestoreReminderMutation,
} from 'graphql/bindings/AdminBindings'
import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  CommentIcon,
  EllipsisVIcon,
  EnvelopeIcon,
  FlagIcon,
  HourglassEndIcon,
  Link,
  NoteFieldRow,
  PhoneIcon,
  SignalIcon,
  SingleInfoCard,
  TimesIcon,
  Typography,
  useModal,
  useTheme,
} from '@forgeglobal/design-framework'
import { ConversationModal } from 'components/shared/ConversationModal'
import { OutreachScheduleModal } from 'components/dashboard/OutreachScheduleModal'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { Routes } from 'util/Routes'
import { getGravatarUrl } from 'util/GravatarUtil'
import React, { Fragment } from 'react'
import moment from 'moment'

interface AccountOutreachMeta {
  AccountOutreach: AccountIdMeta
}

const AccountOutreachNotes: React.FC<WithTodoFragment & {
  accountOpt?: AccountOutreachRollupFragment
  currentUserId: number
}> = ({ todo, accountOpt, currentUserId }) => {
  const theme = useTheme()
  const account = accountOpt
  const lastContactedOpt = account?.last_outreach_at
  const lastContacted = lastContactedOpt && moment(lastContactedOpt)
  const currentSchedule = account?.outreach_schedule
  const currentScheduleDuration = currentSchedule ? moment.duration(currentSchedule) : undefined
  const currentScheduleDays = currentScheduleDuration ? currentScheduleDuration.asDays() : undefined
  const nextOutreachString =
    currentScheduleDuration && lastContacted
      ? lastContacted
          .clone()
          .add(currentScheduleDuration)
          .format('MMMM Do, YYYY')
      : moment()
          .add(currentScheduleDuration)
          .format('MMMM Do, YYYY')
  const completedAt = moment(todo.completed_at || undefined)
  const finishedBy = todo?.finished_by
  const finishedById = finishedBy?.id
  const finishedByMe = finishedById === currentUserId
  const finishedByName = `${finishedBy?.first_name ?? ''} ${finishedBy?.last_name ?? ''}`
  const lastContactedFromNow = lastContacted && moment(lastContacted).fromNow()
  const finishedAtTimeString = moment().isSame(completedAt, 'day')
    ? `today at ${completedAt.format('h:mm a')}`
    : `on ${completedAt.format('M/D/YY')}`
  const lastContactedString = lastContacted ? `Last outreach ${lastContactedFromNow}` : 'No previous outreach'
  const email = account?.email
  const phone = account?.formatted_phone_number
  const [restoreReminder] = useRestoreReminderMutation({
    variables: { reminderId: todo.id },
    refetchQueries: [{ query: AllTodosDocument }],
  })
  const [showModal, hideModal] = useModal(() => {
    return (
      <ConversationModal
        hideModal={hideModal}
        accountName={account?.name}
        formType="create"
        objId={account?.id}
        isAccount
      />
    )
  }, [account])
  const dismissedNote = (
    <NoteFieldRow icon={TimesIcon}>
      You dismissed this reminder.
      <br />
      We will remind you when this task is actionable.
      <br />
      <Typography color="primary" variant="caption">
        <Link onClick={() => restoreReminder()}>
          <b>Restore this reminder.</b>
        </Link>
      </Typography>
    </NoteFieldRow>
  )
  if (todo.status === TodoItemStatusType.Dismissed || !!todo.dismissed_at) {
    return dismissedNote
  }
  switch (todo.status) {
    case TodoItemStatusType.Live:
    case TodoItemStatusType.Upcoming:
      const isAOI = account?.is_account_of_interest ?? false
      const AOIBy = account?.account_of_interest_set_by_admin?.name ?? ''
      const AOIReason = account?.account_of_interest_reason ?? ''
      const AOINote = (!!AOIBy || !!AOIReason) && (
        <NoteFieldRow icon={FlagIcon} iconColor={theme.palette.secondary.main}>
          {AOIBy && `Set by ${AOIBy}. `}
          {AOIReason}
        </NoteFieldRow>
      )
      return (
        <Fragment>
          {isAOI && AOINote}
          {!isAOI && <NoteFieldRow icon={SignalIcon}>Investor level {account?.account_level}</NoteFieldRow>}
          <NoteFieldRow icon={ClockIcon}>
            {lastContactedString}.{' '}
            <Typography color="primary" variant="caption">
              <Link onClick={showModal}>
                <b>Log a conversation.</b>
              </Link>
            </Typography>
          </NoteFieldRow>
          {email && <NoteFieldRow icon={EnvelopeIcon}>{email}</NoteFieldRow>}
          {phone && <NoteFieldRow icon={PhoneIcon}>{phone}</NoteFieldRow>}
        </Fragment>
      )
    case TodoItemStatusType.Expired:
      return <NoteFieldRow icon={HourglassEndIcon}>This reminder has expired.</NoteFieldRow>
    case TodoItemStatusType.Completed:
      return (
        <NoteFieldRow iconColor={theme.palette.secondary.dark} icon={CheckCircleIcon}>
          {finishedByMe ? 'You' : finishedByName} reached out to this account {finishedAtTimeString}.
        </NoteFieldRow>
      )
    case TodoItemStatusType.Rescheduled:
      return (
        <NoteFieldRow icon={CalendarIcon}>
          You rescheduled this reminder.
          <br />
          {currentSchedule
            ? `We will remind you about this task every ${currentScheduleDays} days starting on ${nextOutreachString}.`
            : 'We won’t remind you about this task again.'}
        </NoteFieldRow>
      )
    default:
      return null
  }
}

export const AccountOutreachTodoCard: React.FC<WithTodoFragment> = ({ todo }) => {
  const outreachMeta = JSON.parse(todo.meta) as AccountOutreachMeta
  const { data } = useAccountOutreachRollupQuery({ variables: { accountId: outreachMeta.AccountOutreach.accountId } })
  const account = data?.account
  const isAOI = account?.is_account_of_interest ?? false
  const lastContactedOpt = account?.last_outreach_at
  const lastContacted = lastContactedOpt ? moment(lastContactedOpt) : moment()
  const currentUserId = data?.currentAdmin.id!
  const accountImg = account?.img || getGravatarUrl(account?.email ?? '')
  const [dismissReminder] = useDismissReminderMutation({
    variables: { reminderId: todo.id },
    refetchQueries: [{ query: AllTodosDocument }],
  })
  const currentSchedule = account?.outreach_schedule
  const currentScheduleDuration = currentSchedule ? moment.duration(currentSchedule) : undefined
  const [showModal, hideModal] = useModal(() => {
    return (
      <ConversationModal
        hideModal={hideModal}
        accountName={account?.name}
        formType="create"
        objId={account?.id}
        isAccount
      />
    )
  }, [account])

  const [showScheduleModal, hideScheduleModal] = useModal(() => {
    return (
      <OutreachScheduleModal
        hideModal={hideScheduleModal}
        accountId={account?.id}
        currentSchedule={currentScheduleDuration}
        lastContacted={lastContacted}
      />
    )
  }, [account, currentScheduleDuration, lastContacted])
  const isDone = !!todo.dismissed_at || completedStatuses.includes(todo.status)
  const lastFrontMsgId = account?.last_front_message_id
  const frontUrl = `https://app.frontapp.com/open/${lastFrontMsgId}/`
  const onFrontClick = () => {
    window.open(frontUrl, '_blank')
  }
  const actions = (
    <Fragment>
      <PopoverListItem button onClick={showModal}>
        <PopoverListItemIcon>
          <CommentIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Log Conversation</PopoverListItemText>
      </PopoverListItem>
      {lastFrontMsgId && (
        <PopoverListItem button onClick={onFrontClick}>
          <PopoverListItemIcon>
            <EnvelopeIcon />
          </PopoverListItemIcon>
          <PopoverListItemText>View in Front</PopoverListItemText>
        </PopoverListItem>
      )}
      <PopoverListItem button onClick={showScheduleModal}>
        <PopoverListItemIcon>
          <CalendarIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Manage Schedule</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem button onClick={() => dismissReminder()}>
        <PopoverListItemIcon>
          <TimesIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Dismiss</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )
  return (
    <SingleInfoCard
      logo={accountImg}
      leftTitle={account?.name}
      link={Routes.account(account?.id ?? '')}
      openInNewTab
      secondaryIcon={isAOI && <FlagIcon />}
      leftSubtitle={isAOI ? 'Account of Interest outreach' : 'Investor outreach'}
      notes={<AccountOutreachNotes accountOpt={account} currentUserId={currentUserId} todo={todo} />}
      action={!isDone && <PopoverAction icon={<EllipsisVIcon />} actionMenu={actions} />}
    />
  )
}
